/**
 * Consent plugin: resolves host consent, renders the built-in chooser when
 * ProsPix owns the decision, and records consent UI interactions.
 */
import { readStoredConsent, resolveConsent } from "../lib/consent";
import {
  resolveConsentUiVisibility,
  sanitizePrivacyUrl,
} from "../lib/consent-ui";
import type {
  ConsentSnapshot,
  ConsentUiMode,
  PluginContext,
} from "../lib/types";

import { registerPlugin } from "./register";

const CONSENT_ROOT_ID = "prospix-consent-root";
const OPEN_CONSENT_EVENT = "prospix:open-consent";

function isShopifySite() {
  const shopify = (window as any).Shopify;
  return Boolean(
    window.location.hostname.endsWith(".myshopify.com") ||
    shopify?.customerPrivacy ||
    shopify?.shop ||
    shopify?.routes?.root,
  );
}

function hasHostConsentManager() {
  const win = window as any;
  if (
    typeof win.__tcfapi === "function" ||
    typeof win.__gpp === "function" ||
    win.OneTrust ||
    win.Cookiebot ||
    win.Didomi ||
    win.Osano
  ) {
    return true;
  }

  try {
    if (
      document.querySelector(
        ["#onetrust-banner-sdk", "#CybotCookiebotDialog", "#didomi-host"].join(
          ",",
        ),
      )
    ) {
      return true;
    }

    return Boolean(
      document.querySelector(
        "[data-nosnippet='true'][role='dialog'][aria-label*='cookie' i]",
      ),
    );
  } catch {
    return false;
  }
}

registerPlugin("consent", (ctx: PluginContext) => {
  const consentKey = `prospix_consent_${ctx.siteKey || "default"}`;
  const pageStart = Date.now();
  let rootHost: HTMLElement | null = null;
  let forceOpen = false;
  let initialTimer: ReturnType<typeof setTimeout> | undefined;
  let managerRecheckTimer: ReturnType<typeof setTimeout> | undefined;
  let managerObserver: MutationObserver | undefined;
  let unsubscribeConsent: (() => void) | undefined;

  const sameArray = (a?: number[], b?: number[]) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const removeUi = () => {
    rootHost?.remove();
    rootHost = null;
  };

  const trackInteraction = (action: string, stateBefore: string) => {
    if (!ctx.canTrackCategory("consent_analytics")) return;
    ctx.track("consent_banner_interaction", {
      action,
      cmp_source: "prospix_ui",
      latency_ms: Date.now() - pageStart,
      state_before: stateBefore,
    });
  };

  const getUiConfig = () => {
    const config = ctx.config.privacy.consentUi;
    return {
      mode: config?.mode ?? ("auto" as ConsentUiMode),
      privacyUrl: sanitizePrivacyUrl(config?.privacyUrl, window.location.href),
      showPreferencesButton: config?.showPreferencesButton !== false,
    };
  };

  const createButton = (
    label: string,
    kind: "primary" | "secondary" | "preferences",
    onClick: () => void,
  ) => {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = label;
    button.className = `prospix-consent-${kind}`;
    button.addEventListener("click", onClick);
    return button;
  };

  const renderUi = (snapshot: ConsentSnapshot = ctx.consent) => {
    const uiConfig = getUiConfig();
    const visibility = resolveConsentUiVisibility({
      state: snapshot.state,
      mode: uiConfig.mode,
      gpc: snapshot.gpc,
      dnt: snapshot.dnt,
      isShopify: isShopifySite(),
      hasHostCmp: hasHostConsentManager(),
      forceOpen,
      showPreferencesButton: uiConfig.showPreferencesButton,
    });

    if (visibility === "hidden") {
      removeUi();
      return;
    }

    if (
      rootHost?.isConnected &&
      rootHost.dataset.prospixConsentUi === visibility
    ) {
      return;
    }

    removeUi();
    rootHost = document.createElement("div");
    rootHost.id = CONSENT_ROOT_ID;
    rootHost.dataset.prospixConsentUi = visibility;
    document.body.appendChild(rootHost);

    const shadow = rootHost.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = `
      :host { all: initial; }
      *, *::before, *::after { box-sizing: border-box; }
      .prospix-consent-shell {
        position: fixed;
        z-index: 2147483646;
        left: 18px;
        bottom: 18px;
        width: min(420px, calc(100vw - 36px));
        color: #111827;
        font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 14px;
        line-height: 1.5;
        letter-spacing: 0;
      }
      .prospix-consent-shell.prospix-consent-shell--preferences {
        width: auto;
      }
      .prospix-consent-panel {
        border: 1px solid #d7dedc;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
        padding: 18px;
      }
      .prospix-consent-title {
        margin: 0;
        color: #0f172a;
        font-size: 17px;
        font-weight: 700;
      }
      .prospix-consent-copy {
        margin: 7px 0 0;
        color: #475569;
      }
      .prospix-consent-link {
        display: inline-block;
        margin-top: 9px;
        color: #0f766e;
        font-weight: 650;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      .prospix-consent-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        margin-top: 16px;
      }
      button {
        min-height: 42px;
        border-radius: 7px;
        padding: 9px 14px;
        font: inherit;
        font-weight: 700;
        letter-spacing: 0;
        cursor: pointer;
      }
      button:focus-visible, a:focus-visible {
        outline: 3px solid #f59e0b;
        outline-offset: 2px;
      }
      .prospix-consent-primary {
        border: 1px solid #0f172a;
        background: #0f172a;
        color: #ffffff;
      }
      .prospix-consent-primary:hover { background: #1e293b; }
      .prospix-consent-secondary {
        border: 1px solid #cbd5e1;
        background: #ffffff;
        color: #1e293b;
      }
      .prospix-consent-secondary:hover { background: #f8fafc; }
      .prospix-consent-preferences {
        display: block;
        min-height: 32px;
        border: 1px solid #b8cbc7;
        background: #ffffff;
        color: #0f5f58;
        padding: 6px 10px;
        font-size: 12px;
        line-height: 1.2;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
      }
      @media (max-width: 480px) {
        .prospix-consent-shell { left: 12px; bottom: 12px; width: calc(100vw - 24px); }
        .prospix-consent-actions { grid-template-columns: 1fr; }
      }
      @media (prefers-reduced-motion: reduce) {
        * { scroll-behavior: auto !important; }
      }
    `;
    shadow.appendChild(style);

    const shell = document.createElement("div");
    shell.className = "prospix-consent-shell";
    shadow.appendChild(shell);

    if (visibility === "preferences") {
      shell.classList.add("prospix-consent-shell--preferences");
      shell.appendChild(
        createButton("Privacy choices", "preferences", () => {
          trackInteraction("customize", ctx.consent.state);
          forceOpen = true;
          renderUi(ctx.consent);
        }),
      );
      return;
    }

    const panel = document.createElement("section");
    panel.className = "prospix-consent-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "false");
    panel.setAttribute("aria-labelledby", "prospix-consent-title");
    panel.setAttribute("aria-describedby", "prospix-consent-copy");

    const title = document.createElement("h2");
    title.id = "prospix-consent-title";
    title.className = "prospix-consent-title";
    title.textContent = "Privacy choices";
    panel.appendChild(title);

    const copy = document.createElement("p");
    copy.id = "prospix-consent-copy";
    copy.className = "prospix-consent-copy";
    copy.textContent =
      "We use optional analytics to understand visits and improve this site. Basic visit counts continue without identity or behavior details.";
    panel.appendChild(copy);

    if (uiConfig.privacyUrl) {
      const link = document.createElement("a");
      link.className = "prospix-consent-link";
      link.href = uiConfig.privacyUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = "Privacy policy";
      panel.appendChild(link);
    }

    const choose = (state: "granted" | "denied") => {
      const stateBefore = ctx.consent.state;
      forceOpen = false;
      (window as any).prospix?.consent({
        state,
        cmp: { source: "prospix_ui" },
      });
      trackInteraction(state === "granted" ? "accept" : "reject", stateBefore);
      renderUi({
        ...ctx.consent,
        state,
        cmp: { source: "prospix_ui" },
      });
    };

    const actions = document.createElement("div");
    actions.className = "prospix-consent-actions";
    actions.appendChild(
      createButton("Use basic only", "secondary", () => choose("denied")),
    );
    actions.appendChild(
      createButton("Allow analytics", "primary", () => choose("granted")),
    );
    panel.appendChild(actions);
    shell.appendChild(panel);
  };

  const refresh = async () => {
    const stored = readStoredConsent(ctx.storage.get(consentKey));
    const snapshot = await resolveConsent(ctx.config, stored);
    const current = ctx.consent;
    const changed =
      snapshot.state !== current.state ||
      snapshot.cmp?.tcString !== current.cmp?.tcString ||
      snapshot.cmp?.uspString !== current.cmp?.uspString ||
      snapshot.cmp?.gppString !== current.cmp?.gppString ||
      !sameArray(snapshot.cmp?.gppSectionIds, current.cmp?.gppSectionIds);
    if (changed) {
      (window as any).prospix?.consent({
        state: snapshot.state,
        cmp: snapshot.cmp,
      });
    }
    renderUi(snapshot);
  };

  const selectorActionMap: Array<{ selector: string; action: string }> = [
    { selector: "#onetrust-accept-btn-handler", action: "accept" },
    { selector: "#onetrust-reject-all-handler", action: "reject" },
    { selector: "#onetrust-pc-btn-handler", action: "customize" },
    { selector: "[data-consent-action='accept']", action: "accept" },
    { selector: "[data-consent-action='reject']", action: "reject" },
    { selector: "[data-consent-action='save']", action: "save" },
    { selector: "[data-cookie-action='accept']", action: "accept" },
    { selector: "[data-cookie-action='reject']", action: "reject" },
    { selector: "[data-cookie-action='save']", action: "save" },
  ];

  const onBannerClick = (event: Event) => {
    if (!ctx.canTrackCategory("consent_analytics")) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const match = selectorActionMap.find(({ selector }) =>
      Boolean(target.closest(selector)),
    );
    if (!match) return;

    ctx.track("consent_banner_interaction", {
      action: match.action,
      cmp_source: ctx.consent.cmp?.source,
      latency_ms: Date.now() - pageStart,
      state_before: ctx.consent.state,
    });
  };

  const onOpenConsent = () => {
    forceOpen = true;
    renderUi(ctx.consent);
  };

  return {
    onInit() {
      initialTimer = setTimeout(() => {
        void refresh();
      }, 250);

      managerRecheckTimer = setTimeout(() => {
        renderUi(ctx.consent);
      }, 1500);

      if (typeof MutationObserver !== "undefined") {
        managerObserver = new MutationObserver(() => {
          if (
            rootHost &&
            getUiConfig().mode === "auto" &&
            hasHostConsentManager()
          ) {
            renderUi(ctx.consent);
          }
        });
        managerObserver.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      }

      const tcfapi = (window as any)?.__tcfapi;
      if (tcfapi) {
        try {
          tcfapi("addEventListener", 2, () => {
            void refresh();
          });
        } catch {
          // ignore
        }
      }

      const gpp = (window as any)?.__gpp;
      if (gpp) {
        try {
          gpp("addEventListener", () => {
            void refresh();
          });
        } catch {
          // ignore
        }
      }

      unsubscribeConsent = ctx.on("consent", (payload) => {
        const snapshot = payload as ConsentSnapshot;
        renderUi(snapshot);
      });
      window.addEventListener(OPEN_CONSENT_EVENT, onOpenConsent);
      document.addEventListener("click", onBannerClick, { capture: true });
    },
    onUnload() {
      if (initialTimer) clearTimeout(initialTimer);
      if (managerRecheckTimer) clearTimeout(managerRecheckTimer);
      managerObserver?.disconnect();
      unsubscribeConsent?.();
      window.removeEventListener(OPEN_CONSENT_EVENT, onOpenConsent);
      document.removeEventListener("click", onBannerClick, true);
      removeUi();
    },
  };
});
